import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  limitTypeFormSchema,
  type LimitType,
  type LimitTypeFormValues,
  type UpdateLimitTypePayload,
} from '../types/limitType.types';
import { useUpdateLimitType } from './useUpdateLimitType';

interface UseLimitTypeFormParams {
  limitType: LimitType | null;
  open: boolean;
  onClose: () => void;
}

const EMPTY_DEFAULTS: LimitTypeFormValues = { lowValue: '', highValue: '' };

const toField = (value: number | null): string => (value === null ? '' : String(value));

function toFormValues(limitType: LimitType | null): LimitTypeFormValues {
  if (!limitType) return EMPTY_DEFAULTS;
  return {
    lowValue: toField(limitType.lowValue),
    highValue: toField(limitType.highValue),
  };
}

export function useLimitTypeForm({ limitType, open, onClose }: UseLimitTypeFormParams) {
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<LimitTypeFormValues>({
    resolver: zodResolver(limitTypeFormSchema),
    defaultValues: toFormValues(limitType),
    mode: 'onChange',
  });

  useEffect(() => {
    form.reset(open ? toFormValues(limitType) : EMPTY_DEFAULTS);
  }, [limitType, open, form]);

  const updateMutation = useUpdateLimitType(onClose);

  function submit(values: LimitTypeFormValues) {
    if (!limitType) return;

    const changedKeys = Object.keys(form.formState.dirtyFields) as (keyof LimitTypeFormValues)[];
    if (changedKeys.length === 0) {
      onClose();
      return;
    }

    const payload: UpdateLimitTypePayload = {};
    for (const key of changedKeys) {
      if (values[key] !== '') payload[key] = Number(values[key]);
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    updateMutation.mutate({ id: limitType.id, payload });
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  return {
    form,
    isPending: updateMutation.isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard: () => {
      setIsDiscardDialogOpen(false);
      onClose();
    },
    cancelDiscard: () => setIsDiscardDialogOpen(false),
  };
}
